import { cn } from '../../lib/utils';
import type { SortDirection } from '../../hooks/useRankings';

interface SortableHeaderProps {
  readonly label: string;
  /** Current sort direction for THIS column; null = not the active sort. */
  readonly sortDir: SortDirection | null;
  readonly onSort: () => void;
  /** Numeric columns are right-aligned, severity center-aligned, default left. */
  readonly align?: 'left' | 'right' | 'center';
  /** Optional fixed column width (Tailwind class). */
  readonly widthClass?: string;
}

/**
 * <th> with a click-to-sort button inside. Mirrors `_archive/rankings.html`
 * lines 162-184:
 *
 * - Header is `text-[12px] font-semibold uppercase tracking-[0.05em]`.
 * - Single up-caret `▲` always rendered, but rotated 180° when the
 *   column is sorted descending. The prototype uses the same single-glyph
 *   trick (line 184) instead of swapping between ▲ and ▼.
 * - Caret opacity (IMP-004, 2026-06-01): 0.4 default → 0.7 hover → 1
 *   when this column is the active sort. The default-state opacity was
 *   bumped from 0 to 0.4 because Kate flagged on the 6/1 Zoom that she
 *   couldn't tell columns were sortable -- with no resting affordance
 *   she had to mouse over each header to discover the click target.
 *   0.4 keeps inactive columns visually subordinate to the active sort
 *   while still surfacing the affordance at a glance.
 *
 * `aria-sort` reflects direction so screen readers announce the sort
 * state on every render.
 *
 * RANK-fix-7 (code-review apply): added `'center'` alignment so the
 * Severity column header truly centers (prototype line 187
 * `text-align: center`). The previous union of `'left' | 'right'`
 * forced center columns to render left-aligned headers while body
 * cells stayed center-aligned — a small but real misalignment.
 */
export function SortableHeader({ label, sortDir, onSort, align = 'left', widthClass }: SortableHeaderProps) {
  const ariaSort = sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none';
  const isActive = sortDir !== null;
  const alignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={cn(
        'group h-9 cursor-pointer select-none border-b-2 border-navy bg-white px-3 text-[13px] font-semibold text-text-3 transition-colors hover:text-navy',
        alignClass,
        widthClass,
      )}
    >
      {/* CR-32 (Maria 2026-07-08 evening — "headers and content still
          misaligned"): browsers give <button> a DEFAULT text-align:center
          that the th's text-left does NOT override. Single-line headers
          looked fine (the inline-flex button hugs its content at the th's
          left padding edge), but headers that WRAP (e.g. "Springboard
          occupational group") had their lines centered — measured 22-28px
          right of the cell text below. The CR-07 fix put text-left on the
          th only, and its test asserted the th class, so it passed while
          the pixels stayed centered. The alignment class must be ON THE
          BUTTON, where the text actually lives. */}
      <button
        type="button"
        onClick={onSort}
        className={cn(
          'inline-flex items-center gap-1 transition',
          alignClass,
          isActive ? 'text-navy' : '',
        )}
      >
        <span>{label}</span>
        <span
          aria-hidden="true"
          className={cn(
            'inline-block text-[10px] leading-none transition-all',
            // IMP-004: resting state goes from opacity-0 → opacity-40
            // so inactive sortable columns advertise themselves
            // without requiring a hover. Active column still saturates
            // to opacity-100, hover bumps to opacity-70 so the caret
            // strengthens when the cursor lands on the header.
            isActive ? 'opacity-100' : 'opacity-40 group-hover:opacity-70',
            sortDir === 'desc' ? 'rotate-180' : '',
          )}
        >
          ▲
        </span>
      </button>
    </th>
  );
}
